import { CheckOutlined, DeleteOutlined, EditOutlined, PlayCircleOutlined } from '@ant-design/icons';
import {
  Button,
  Empty,
  Form,
  Input,
  Modal,
  Popconfirm,
  Select,
  Space,
  Switch,
  Table,
  Typography,
  message,
} from 'antd';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type {
  CreateLinkRuleRequest,
  ItemType,
  ItemTypeListItem,
  JiraLinkDirection,
  LinkRule,
  MatchRule,
  MatchRuleMode,
  UpdateLinkRuleRequest,
} from '@lens/shared';
import { getItemType } from '../item-types/api.ts';
import { listIssueLinkTypes, type JiraIssueLinkType } from '../jira/api.ts';
import { MatchRulesEditor, type MatchRuleRow } from './MatchRulesEditor.tsx';
import {
  applyLinkRuleBulk,
  createLinkRule,
  deleteLinkRule,
  listLinkRules,
  updateLinkRule,
} from './api.ts';

interface LinkRulesPanelProps {
  spaceId: string;
  itemType: ItemType;
  allItemTypes: ItemTypeListItem[];
  myRole: 'admin' | 'member' | 'viewer';
}

interface LinkRuleFormValues {
  name: string;
  targetItemTypeId: string;
  linkType: string;
  jiraLinkDirection: JiraLinkDirection;
  pushEnabled: boolean;
}

const { Title } = Typography;

function toMatchRuleRows(matchRules: MatchRule[]): MatchRuleRow[] {
  return matchRules.map((rule) => ({
    sourceField: rule.sourceField ?? '',
    targetField: rule.targetField ?? '',
    mode: (rule.mode ?? 'eq') as MatchRuleMode,
    required: rule.required ?? false,
    lookupColumn: rule.lookupColumn,
  }));
}

function normalizeMatchRules(matchRules: MatchRuleRow[]): MatchRule[] {
  return matchRules
    .filter((rule) => rule.sourceField && rule.targetField)
    .map((rule) => ({
      sourceField: rule.sourceField,
      targetField: rule.targetField,
      mode: rule.mode,
      required: rule.required || undefined,
      lookupColumn: rule.lookupColumn || undefined,
    }));
}

export function LinkRulesPanel({
  spaceId,
  itemType,
  allItemTypes,
  myRole,
}: LinkRulesPanelProps): JSX.Element {
  const isAdmin = myRole === 'admin';
  const [form] = Form.useForm<LinkRuleFormValues>();
  const [rules, setRules] = useState<LinkRule[]>([]);
  const [loading, setLoading] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingRule, setEditingRule] = useState<LinkRule | null>(null);
  const [saving, setSaving] = useState(false);
  const [applyingRuleId, setApplyingRuleId] = useState<string | null>(null);
  const [deletingRuleId, setDeletingRuleId] = useState<string | null>(null);
  const [targetItemTypeId, setTargetItemTypeId] = useState<string>('');
  const [targetFields, setTargetFields] = useState<ItemType['fields']>([]);
  const [matchRules, setMatchRules] = useState<MatchRuleRow[]>([]);
  const [jiraLinkTypes, setJiraLinkTypes] = useState<JiraIssueLinkType[]>([]);
  // Ведём выбранный тип связи через Form.useWatch, чтобы подписи направления
  // (inward/outward) брались из выбранного Jira-типа.
  const selectedLinkType = Form.useWatch('linkType', form);

  const targetItemTypeOptions = useMemo(
    () =>
      allItemTypes
        .filter((candidate) => candidate.id !== itemType.id)
        .map((candidate) => ({
          value: candidate.id,
          label: `${candidate.name} (${candidate.slug})`,
        })),
    [allItemTypes, itemType.id],
  );

  const targetTypeNameById = useMemo(
    () => new Map(allItemTypes.map((candidate) => [candidate.id, candidate.name])),
    [allItemTypes],
  );

  const loadRules = useCallback(async (): Promise<void> => {
    setLoading(true);
    try {
      const nextRules = await listLinkRules(spaceId, itemType.id);
      setRules(nextRules);
    } catch (err) {
      setRules([]);
      void message.error(err instanceof Error ? err.message : 'Ошибка');
    } finally {
      setLoading(false);
    }
  }, [spaceId, itemType.id]);

  useEffect(() => {
    setRules([]);
    void loadRules();
  }, [loadRules]);

  // Список типов связей Jira — для пикера «Тип связи». Грузим при первом
  // открытии редактора; если Jira недоступна, список пуст (Select всё равно
  // покажет текущее значение).
  useEffect(() => {
    if (!editorOpen || jiraLinkTypes.length > 0) return;
    let cancelled = false;
    void listIssueLinkTypes()
      .then((types) => { if (!cancelled) setJiraLinkTypes(types); })
      .catch(() => { /* Jira может быть не настроена — не критично */ });
    return () => { cancelled = true; };
  }, [editorOpen, jiraLinkTypes.length]);

  const linkTypeOptions = useMemo(
    () => jiraLinkTypes.map((t) => ({ value: t.name, label: t.name })),
    [jiraLinkTypes],
  );

  // Подписи направления берём из выбранного Jira-типа (outward/inward),
  // с дефолтами, если тип не из справочника Jira.
  const directionOptions = useMemo(() => {
    const t = jiraLinkTypes.find((x) => x.name === selectedLinkType);
    return [
      { value: 'outward', label: t ? `Прямое — «${t.outward}»` : 'Прямое (текущая → цель)' },
      { value: 'inward', label: t ? `Обратное — «${t.inward}»` : 'Обратное (цель → текущая)' },
    ];
  }, [jiraLinkTypes, selectedLinkType]);

  useEffect(() => {
    if (!editorOpen || !targetItemTypeId) {
      setTargetFields([]);
      return;
    }

    let cancelled = false;

    const loadTargetItemType = async (): Promise<void> => {
      try {
        const targetItemType = await getItemType(spaceId, targetItemTypeId);
        if (!cancelled) {
          setTargetFields(targetItemType.fields ?? []);
        }
      } catch (err) {
        if (!cancelled) {
          setTargetFields([]);
          void message.error(err instanceof Error ? err.message : 'Ошибка');
        }
      }
    };

    void loadTargetItemType();

    return () => {
      cancelled = true;
    };
  }, [editorOpen, spaceId, targetItemTypeId]);

  const closeEditor = (): void => {
    setEditorOpen(false);
    setEditingRule(null);
    setTargetItemTypeId('');
    setTargetFields([]);
    setMatchRules([]);
    form.resetFields();
  };

  const openCreateModal = (): void => {
    setEditingRule(null);
    setTargetItemTypeId('');
    setTargetFields([]);
    setMatchRules([]);
    form.setFieldsValue({
      name: '',
      targetItemTypeId: undefined,
      linkType: 'Relates',
      jiraLinkDirection: 'outward',
      pushEnabled: true,
    });
    setEditorOpen(true);
  };

  const openEditModal = (rule: LinkRule): void => {
    setEditingRule(rule);
    setTargetItemTypeId(rule.targetItemTypeId);
    setMatchRules(toMatchRuleRows(rule.matchRules));
    form.setFieldsValue({
      name: rule.name,
      targetItemTypeId: rule.targetItemTypeId,
      linkType: rule.linkType,
      jiraLinkDirection: rule.jiraLinkDirection,
      pushEnabled: rule.pushEnabled,
    });
    setEditorOpen(true);
  };

  const handleSubmit = async (): Promise<void> => {
    const values = await form.validateFields();
    setSaving(true);
    try {
      if (editingRule) {
        const payload: UpdateLinkRuleRequest = {
          name: values.name,
          linkType: values.linkType,
          jiraLinkDirection: values.jiraLinkDirection,
          pushEnabled: values.pushEnabled,
          matchRules: normalizeMatchRules(matchRules),
        };
        await updateLinkRule(spaceId, itemType.id, editingRule.id, payload);
      } else {
        const payload: CreateLinkRuleRequest = {
          targetItemTypeId: values.targetItemTypeId,
          name: values.name,
          linkType: values.linkType,
          jiraLinkDirection: values.jiraLinkDirection,
          pushEnabled: values.pushEnabled,
          matchRules: normalizeMatchRules(matchRules),
        };
        await createLinkRule(spaceId, itemType.id, payload);
      }

      closeEditor();
      await loadRules();
    } catch (err) {
      void message.error(err instanceof Error ? err.message : 'Ошибка');
    } finally {
      setSaving(false);
    }
  };

  const handleApplyRule = async (ruleId: string): Promise<void> => {
    setApplyingRuleId(ruleId);
    try {
      const result = await applyLinkRuleBulk(spaceId, itemType.id, ruleId, {});
      void message.success(
        `Обработано: ${result.itemsProcessed}, создано связей: ${result.linksCreated}`,
      );
    } catch (err) {
      void message.error(err instanceof Error ? err.message : 'Ошибка');
    } finally {
      setApplyingRuleId(null);
    }
  };

  const handleDeleteRule = async (ruleId: string): Promise<void> => {
    setDeletingRuleId(ruleId);
    try {
      await deleteLinkRule(spaceId, itemType.id, ruleId);
      await loadRules();
    } catch (err) {
      void message.error(err instanceof Error ? err.message : 'Ошибка');
    } finally {
      setDeletingRuleId(null);
    }
  };

  return (
    <div style={{ marginTop: 24 }}>
      <Space style={{ marginBottom: 12, width: '100%', justifyContent: 'space-between' }}>
        <Title level={5} style={{ margin: 0 }}>
          Правила связей
        </Title>
        {isAdmin && (
          <Button type="primary" size="small" onClick={openCreateModal}>
            + Правило
          </Button>
        )}
      </Space>

      {rules.length === 0 && !loading ? (
        <Empty description="Нет правил связывания" />
      ) : (
        <Table<LinkRule>
          rowKey="id"
          dataSource={rules}
          loading={loading}
          pagination={false}
          size="small"
          columns={[
            {
              title: 'Имя',
              dataIndex: 'name',
              key: 'name',
            },
            {
              title: 'Цель',
              key: 'targetItemTypeId',
              render: (_value, rule) =>
                targetTypeNameById.get(rule.targetItemTypeId) ?? rule.targetItemTypeId,
            },
            {
              title: 'Тип связи',
              dataIndex: 'linkType',
              key: 'linkType',
              render: (_value, rule) =>
                `${rule.linkType}${rule.jiraLinkDirection === 'inward' ? ' (обратное)' : ''}`,
            },
            {
              title: 'Правил совпадения',
              key: 'matchRules',
              render: (_value, rule) => rule.matchRules.length,
            },
            {
              title: 'Push в Jira',
              key: 'pushEnabled',
              render: (_value, rule) => (rule.pushEnabled ? <CheckOutlined /> : '—'),
            },
            ...(isAdmin
              ? [
                  {
                    title: 'Действия',
                    key: 'actions',
                    render: (_value: unknown, rule: LinkRule) => (
                      <Space size={0}>
                        <Button
                          type="text"
                          size="small"
                          icon={<EditOutlined />}
                          title="Изменить"
                          onClick={() => openEditModal(rule)}
                        />
                        <Popconfirm
                          title="Связать все записи по этому правилу?"
                          okText="Связать"
                          cancelText="Отмена"
                          onConfirm={() => void handleApplyRule(rule.id)}
                        >
                          <Button
                            type="text"
                            size="small"
                            icon={<PlayCircleOutlined />}
                            title="Связать все записи"
                            loading={applyingRuleId === rule.id}
                          />
                        </Popconfirm>
                        <Popconfirm
                          title="Удалить правило?"
                          okText="Удалить"
                          cancelText="Отмена"
                          okButtonProps={{ danger: true }}
                          onConfirm={() => void handleDeleteRule(rule.id)}
                        >
                          <Button
                            type="text"
                            size="small"
                            danger
                            icon={<DeleteOutlined />}
                            title="Удалить"
                            loading={deletingRuleId === rule.id}
                          />
                        </Popconfirm>
                      </Space>
                    ),
                  },
                ]
              : []),
          ]}
        />
      )}

      <Modal
        title={editingRule ? 'Изменить правило' : 'Новое правило'}
        open={editorOpen}
        onCancel={closeEditor}
        onOk={() => void handleSubmit()}
        okText={editingRule ? 'Сохранить' : 'Создать'}
        cancelText="Отмена"
        confirmLoading={saving}
        width={840}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            linkType: 'Relates',
            jiraLinkDirection: 'outward',
            pushEnabled: true,
          }}
        >
          <Form.Item
            name="name"
            label="Имя"
            rules={[{ required: true, message: 'Введите имя правила' }]}
          >
            <Input placeholder="Например, По владельцу" />
          </Form.Item>

          <Form.Item
            name="targetItemTypeId"
            label="Целевой тип"
            rules={[{ required: true, message: 'Выберите целевой тип' }]}
          >
            <Select
              placeholder="Выберите тип записи"
              options={targetItemTypeOptions}
              disabled={!!editingRule}
              onChange={(nextValue) => {
                setTargetItemTypeId(nextValue);
                setMatchRules([]);
              }}
            />
          </Form.Item>

          <Form.Item
            name="linkType"
            label="Тип связи (Jira)"
            rules={[{ required: true, message: 'Укажите тип связи' }]}
            extra={jiraLinkTypes.length > 0
              ? 'Каноничное имя типа связи Jira (напр. «Включение», «Relates»), не inward/outward-подпись.'
              : 'Типы Jira не загрузились — введите каноничное имя типа вручную (напр. «Включение», «Relates»), не inward/outward-подпись.'}
          >
            {jiraLinkTypes.length > 0 ? (
              <Select
                placeholder="Выберите тип связи Jira"
                options={linkTypeOptions}
                showSearch
                optionFilterProp="label"
              />
            ) : (
              // Фолбэк: если список типов Jira не пришёл — не блокируем правку,
              // даём свободный ввод (как было до пикера).
              <Input placeholder="Например, Включение или Relates" />
            )}
          </Form.Item>

          <Form.Item
            name="jiraLinkDirection"
            label="Направление push"
            rules={[{ required: true, message: 'Выберите направление' }]}
            extra="Какую сторону связи создавать в Jira от текущей записи."
          >
            <Select options={directionOptions} />
          </Form.Item>

          <Form.Item
            name="pushEnabled"
            label="Push в Jira"
            valuePropName="checked"
            extra="Выкл. — Lens создаёт связь только у себя и не пушит её в Jira (напр. эти связи ведёт автоматизация Jira)."
          >
            <Switch />
          </Form.Item>

          <MatchRulesEditor
            sourceFields={itemType.fields}
            targetFields={targetFields}
            value={matchRules}
            onChange={setMatchRules}
          />
        </Form>
      </Modal>
    </div>
  );
}
